import History from '@history';
import { createAsyncThunk, EntityId } from '@reduxjs/toolkit';
import { userDataSelector } from 'app/auth/store/user.selector';
import { SMS_NOT_AVAILABLE_IN_COUNTRY_ERROR } from 'app/constants/app.constant';
import { routesUrl } from 'app/fuse-configs/navigationConfig';
import { setFranchiseSmsPrice } from 'app/main/apps/franchises/ducks/franchises.slice';
import jwtService from 'app/services/jwtService';
import { AppDispatch, RootState } from 'app/store';
import { showMessage } from 'app/store/fuse/messageSlice';
import i18n from 'i18n';
import moment from 'moment';
import { sendMedia } from '../../../../services/mediaService/mediaService';
import { getUserClientsApi } from '../../../pages/clients/api';
import {
  AcquiredFromTypeEnum,
  ClientsSortOrderEnum,
  ClientsSortTypeEnum,
  ClientType,
} from '../../../pages/clients/type';
import { getUserCouponApi } from '../../automatedMarketing/api';
import { selectUserFranchise } from '../../franchises/ducks/franchises.selector';
import { selectStoresEntities } from '../../stores/ducks/stores.selector';
import {
  cancelCommunicationCampaignApi,
  createCommunicationCampaignApi,
  getCampaignCouponClaimsApi,
  getCommunicationCampaignCouponApi,
  getCommunicationCampaignInfoApi,
  getCommunicationCampaignNotifiedUsersApi,
  getCommunicationCampaignStatsApi,
  getNewsByIdApi,
  getNotificationCampaignSMSCostApi,
  getNotificationCampaignsRevenueApi,
  updateCommunicationCampaignApi,
} from '../api/campaign.api';
import {
  CommunicationCampaignDispatchStatus,
  CommunicationCampaignOutDTO,
  CurrentCampaign,
  NotificationType,
} from '../campaign.type';
import { displayedDispatchStatus } from '../utils/displayedDispatchStatus';
import { toApiCampaignFilters } from '../utils/filterPayload';
import { detectFilterTypes } from '../utils/filterTypes';
import {
  campaignFiltersSelector,
  currentCouponSelector,
  fullCampaignSelector,
  hasClientsFilterSelector,
  selectNotifiedUsers,
  selectUsers,
} from './notification.selector';
import {
  actions,
  resetCurrent,
  setCampaignValidationDialog,
  updateAdvancedFiltersValid,
  updateCurrentCampaign,
  updateCurrentCampaignFilters,
} from './notification.slice';

export const updateCampaignFilters =
  (values: Partial<CommunicationCampaignOutDTO['campaignFilters']>) => (dispatch: AppDispatch) => {
    dispatch(
      updateCurrentCampaignFilters({
        ...values,
      }),
    );
  };

export const updateCampaignTypeThunk =
  (type: NotificationType) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const campaignFilters = campaignFiltersSelector(state);
    const stores = selectStoresEntities(state);
    const user = userDataSelector(state);
    try {
      dispatch(
        updateCurrentCampaign({
          type,
        }),
      );
      if (type === NotificationType.sms) {
        const storesIdsSelectable = campaignFilters.storeIds.filter(storeId => stores[storeId]?.ctNum || user.stripeId);
        dispatch(updateCurrentCampaignFilters({ storeIds: storesIdsSelectable }));
        if (storesIdsSelectable.length < campaignFilters.storeIds.length) {
          // Full removal (no eligible store left) vs partial removal need different copy.
          const messageKey =
            storesIdsSelectable.length === 0 ? 'validation.smsStoresRemoved' : 'validation.smsStoresPartiallyRemoved';
          dispatch(
            showMessage({
              message: i18n.t(messageKey, { ns: 'communication' }),
              variant: 'warning',
            }),
          );
        }
        dispatch(getSMSCostInfo());
      } else {
        dispatch(removeSMSCostInfo());
      }
    } catch (_error) {
      dispatch(showMessage({ message: i18n.t('validation.error', { ns: 'communication' }), variant: 'error' }));
    }
  };

export const getNotificationCampaignsThunk = createAsyncThunk(
  'notificationCampaign/getNotificationCampaigns',
  async (_, { rejectWithValue }) => {
    try {
      const userId = jwtService.getUserId();
      if (!userId) {
        return rejectWithValue('No user id');
      }
      const notificationCampaignsDTOs = await getNotificationCampaignsRevenueApi(userId);
      notificationCampaignsDTOs.forEach(campaign => {
        campaign.dispatchStatus = displayedDispatchStatus(campaign);
      });
      const sortedCampaigns = notificationCampaignsDTOs.sort((a, b) => moment(b.dateCreated).diff(a.dateCreated));
      return sortedCampaigns;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

let latestSMSCostRequestId = 0;

export const getSMSCostInfo = createAsyncThunk(
  'notificationCampaign/getSMSCostInfo',
  async (_, { dispatch, getState }) => {
    const requestId = ++latestSMSCostRequestId;
    const isStale = () => requestId !== latestSMSCostRequestId;
    try {
      const state = getState() as RootState;
      dispatch(actions.setCampaignsAreLoading(true));
      const campaignFilters = campaignFiltersSelector(state);
      const franchise = selectUserFranchise(state);
      const smsCosts = await getNotificationCampaignSMSCostApi(franchise.id, toApiCampaignFilters(campaignFilters));
      if (isStale()) {
        return;
      }
      dispatch(
        actions.updateCurrentCampaignSMS({
          smsCost: smsCosts.totalPrice,
          numberSMS: smsCosts.nbUsers,
          singleSMSCost: smsCosts.unitPrice,
          numberPartialClients: smsCosts.nbPartialClients ?? 0,
        }),
      );
    } catch (error: any) {
      if (isStale()) {
        return;
      }
      if (error?.response?.data?.error?.status === SMS_NOT_AVAILABLE_IN_COUNTRY_ERROR) {
        dispatch(setFranchiseSmsPrice({ isAvailable: false, unitPrice: null }));
        dispatch(actions.clearCurrentCampaignSMS());
      } else {
        dispatch(actions.getCampaignsFailed());
      }
    } finally {
      if (!isStale()) {
        dispatch(actions.setCampaignsAreLoading(false));
      }
    }
  },
);

export const removeSMSCostInfo = createAsyncThunk('notificationCampaign/removeSMSCostInfo', async (_, { dispatch }) => {
  try {
    dispatch(actions.setCampaignsAreLoading(true));
    dispatch(actions.clearCurrentCampaignSMS());
  } catch (_error) {
    dispatch(actions.getCampaignsFailed());
  } finally {
    dispatch(actions.setCampaignsAreLoading(false));
  }
});

let latestNotifiedUsersRequestId = 0;

export const getCommunicationCampaignsNotifiedUsers =
  (notificationType?: NotificationType) => async (dispatch: AppDispatch, getState: () => RootState) => {
    // Filters change on every keystroke, so several requests can overlap and answer out of order:
    // only the most recent one is allowed to write to the store.
    const requestId = ++latestNotifiedUsersRequestId;
    const isStale = () => requestId !== latestNotifiedUsersRequestId;
    try {
      dispatch(actions.setNotifiedUsersLoading(true));
      const campaignFilters = campaignFiltersSelector(getState());
      const franchise = selectUserFranchise(getState());
      const notifiedUsers = await getCommunicationCampaignNotifiedUsersApi(
        toApiCampaignFilters(campaignFilters),
        franchise.id,
        notificationType,
      );
      if (isStale()) {
        return;
      }
      dispatch(actions.setNotifiedUsers(notifiedUsers));
    } catch (_error: any) {
      if (isStale()) {
        return;
      }
      dispatch(showMessage({ message: i18n.t('pages.notificationCampaign.notifiedUsersError'), variant: 'error' }));
    } finally {
      if (!isStale()) {
        dispatch(actions.setNotifiedUsersLoading(false));
        if (hasClientsFilterSelector(getState())) {
          const notificationCount = selectNotifiedUsers(getState());
          const selectedClients = selectUsers(getState());
          dispatch(updateAdvancedFiltersValid(notificationCount >= selectedClients.length));
        }
      }
    }
  };

export const createNewCampaign = createAsyncThunk(
  'notificationCampaign/createCampaign',
  async (_, { dispatch, getState }: { dispatch: any; getState: any }) => {
    try {
      const userId = jwtService.getUserId();

      if (!userId) {
        throw new Error('No user id');
      }
      const campaign = fullCampaignSelector(getState());
      const coupon = currentCouponSelector(getState());
      const franchise = selectUserFranchise(getState());

      dispatch(setCampaignValidationDialog({ isLoading: true }));

      let s3Url: string | undefined;
      if (campaign.imageUrl && !campaign.imageUrl.startsWith('https')) {
        const data = await fetch(campaign.imageUrl);
        const blob = await data.blob();
        s3Url = await sendMedia(blob);
      }

      const notificationCampaignDTO: CurrentCampaign = {
        communicationCampaign: {
          ...campaign.communicationCampaign,
          userId,
          notification: {
            ...campaign.communicationCampaign.notification,
            imageUrl: s3Url || campaign.imageUrl,
          },
          campaignFilters: toApiCampaignFilters(campaign.communicationCampaign.campaignFilters),
          points: campaign.communicationCampaign.points,
        },
        news: {
          ...campaign.news,
          imageUrl: s3Url || campaign.imageUrl,
          link: campaign.news.link || undefined,
        },
        coupon,
        isNewsDifferentFromCampaign: false,
        hasCoupon: false,
        points: campaign.points,
        hasLink: false,
        isSMSLengthValid: false,
        merchantUserId: userId,
        startTime: campaign.startTime,
        endTime: moment(campaign.endTime).toISOString(),
        isSent: false,
        isTemplate: false,
      };

      const { communicationCampaign } = await createCommunicationCampaignApi(franchise.id, notificationCampaignDTO);
      dispatch(actions.updateCampaign(communicationCampaign));
      dispatch(setCampaignValidationDialog({ hasBeenSaved: true }));
      dispatch(showMessage({ message: i18n.t('validation.success', { ns: 'communication' }), variant: 'success' }));
      dispatch(actions.closeCampaignValidationDialog());
      History.push(routesUrl.campaignHistory);
      dispatch(resetCurrent());
    } catch (error: any) {
      if (error.response?.data?.detail === 'SENDER_ID_NOT_VALID')
        dispatch(
          showMessage({ message: i18n.t('validation.errorSenderId', { ns: 'communication' }), variant: 'error' }),
        );
      else
        dispatch(
          showMessage({ message: i18n.t('validation.errorCampaign', { ns: 'communication' }), variant: 'error' }),
        );
    } finally {
      dispatch(setCampaignValidationDialog({ isLoading: false }));
    }
  },
);

export const updateCampaignThunk = createAsyncThunk(
  'notificationCampaign/updateCampaign',
  async (campaign: CurrentCampaign, { dispatch }: { dispatch: any; getState: any }) => {
    try {
      dispatch(setCampaignValidationDialog({ isLoading: true }));
      if (!campaign.id) {
        throw new Error('No campaign id');
      }

      let s3Url;
      if (campaign.imageUrl && !campaign.imageUrl.startsWith('https')) {
        const data = await fetch(campaign.imageUrl);
        const blob = await data.blob();
        s3Url = await sendMedia(blob);
      }

      const campaignDto: CurrentCampaign = {
        id: campaign.id,
        isTemplate: campaign.isTemplate,
        communicationCampaign: {
          ...campaign.communicationCampaign,
          campaignFilters: toApiCampaignFilters(campaign.communicationCampaign.campaignFilters),
          notification: {
            ...campaign.communicationCampaign.notification,
            imageUrl: s3Url || campaign.imageUrl,
          },
          points: campaign.communicationCampaign.points,
        },
        news: {
          ...campaign.news,
          imageUrl: s3Url || campaign.imageUrl,
          link: campaign.news.link || undefined,
        },
        coupon: campaign.coupon,
        isNewsDifferentFromCampaign: false,
        hasCoupon: false,
        points: campaign.points,
        hasLink: false,
        isSMSLengthValid: false,
        merchantUserId: campaign.merchantUserId,
        startTime: !campaign.isSent ? campaign.startTime : undefined,
        endTime: moment(campaign.endTime).toISOString(),
        isSent: false,
      };

      const updated = await updateCommunicationCampaignApi(campaign.id, campaignDto);
      dispatch(actions.updateCampaign(updated));
      await dispatch(setCampaignValidationDialog({ hasBeenSaved: true }));
      dispatch(showMessage({ message: i18n.t('validation.editSuccess', { ns: 'communication' }), variant: 'success' }));
      dispatch(actions.closeCampaignValidationDialog());
      dispatch(resetCurrent());
      History.push(routesUrl.campaignHistory);
    } catch (_error) {
      dispatch(showMessage({ message: i18n.t('validation.editError', { ns: 'communication' }), variant: 'error' }));
    } finally {
      dispatch(setCampaignValidationDialog({ isLoading: false }));
    }
  },
);

export const cancelCommunicationCampaign = createAsyncThunk(
  'notificationCampaign/cancelCampaign',
  async (campaignId: EntityId, { dispatch }: { dispatch: any }) => {
    try {
      const response = await cancelCommunicationCampaignApi(campaignId);
      dispatch(actions.updateCampaign({ id: campaignId, changes: { state: response.state } }));
      dispatch(showMessage({ message: i18n.t('pages.notificationCampaign.cancelDialog.success'), variant: 'success' }));
    } catch (_error: any) {
      dispatch(showMessage({ message: i18n.t('pages.notificationCampaign.cancelDialog.error'), variant: 'error' }));
    }
  },
);

export const getCommunicationCampaignInfoThunk = createAsyncThunk(
  'notificationCampaign/getCampaignInfo',
  async (campaignId: EntityId, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await getCommunicationCampaignInfoApi(campaignId);
      return data;
    } catch (e: any) {
      if (e.response.data.error.status === 'COMMUNICATION_CAMPAIGN_NOT_FOUND') {
        dispatch(
          showMessage({
            message: i18n.t('selectedCampaign.errors.notFound', { ns: 'communication' }),
            variant: 'error',
          }),
        );
        return undefined;
      }
      dispatch(
        showMessage({ message: i18n.t('selectedCampaign.errors.generic', { ns: 'communication' }), variant: 'error' }),
      );
      return rejectWithValue(e.response.status);
    }
  },
);

export const getCommunicationCampaignStatsThunk = createAsyncThunk(
  'notificationCampaign/getCampaignRevenue',
  async (campaignId: EntityId, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await getCommunicationCampaignStatsApi(campaignId);
      return data;
    } catch (error: any) {
      dispatch(
        showMessage({ message: i18n.t('selectedCampaign.errors.generic', { ns: 'communication' }), variant: 'error' }),
      );
      return rejectWithValue(error.response.status);
    }
  },
);

export const getCampaignCouponClaimsThunk = createAsyncThunk(
  'notificationCampaign/getCampaignCouponClaims',
  ({ campaignId, page, size }: { campaignId: EntityId; page: number; size: number }) =>
    getCampaignCouponClaimsApi(campaignId, page, size),
);

export const getCurrentCampaignThunk = createAsyncThunk(
  'notificationCampaign/getCurrentCampaign',
  async (campaignId: EntityId, { dispatch, rejectWithValue }) => {
    try {
      const { data: campaignInfo } = await getCommunicationCampaignInfoApi(campaignId);
      const news = campaignInfo.newsId
        ? await getNewsByIdApi(campaignInfo.newsId)
        : {
            title: '',
            message: '',
            startDate: '',
            endDate: '',
            dateCreated: '',
          };
      let coupon;
      if (campaignInfo.currentCouponId) {
        coupon = await getUserCouponApi(campaignId.toString());
      }
      const isNewsDifferentFromCampaign =
        campaignInfo.type === NotificationType.sms && news.message !== campaignInfo.notification?.message;
      const currentCampaign: CurrentCampaign = {
        id: campaignInfo.id,
        communicationCampaign: {
          ...campaignInfo,
          id: campaignInfo.id.toString(),
          userId: campaignInfo.userId.toString(),
          notification: {
            ...(isNewsDifferentFromCampaign ? campaignInfo.notification || news : news),
          },
          points: campaignInfo.points,
        },
        news,
        coupon,
        isNewsDifferentFromCampaign,
        hasCoupon: !!campaignInfo.currentCouponId,
        points: campaignInfo.points,
        imageUrl: news.imageUrl,
        hasLink: !!news.link,
        isSMSLengthValid: true,
        merchantUserId: campaignInfo.userId.toString(),
        startTime: {
          date: moment(campaignInfo.startDate).format(moment.HTML5_FMT.DATE),
          hour: moment(news.startDate).hour(),
        },
        endTime: moment(campaignInfo.endDate).toISOString(),
        isSent: !!(campaignInfo.dispatchStatus === CommunicationCampaignDispatchStatus.SENT),
        isTemplate: false,
      };
      dispatch(actions.updateCurrentFullCampaign(currentCampaign));
      dispatch(
        actions.setCurrentCampaignFiltersTypes(
          detectFilterTypes(currentCampaign.communicationCampaign.campaignFilters),
        ),
      );
      return campaignInfo;
    } catch (error: any) {
      if (error.response.status === 404) {
        dispatch(
          showMessage({
            message: i18n.t('selectedCampaign.errors.notFound', { ns: 'communication' }),
            variant: 'error',
          }),
        );
        History.goBack();
        return undefined;
      }
      dispatch(
        showMessage({ message: i18n.t('selectedCampaign.errors.generic', { ns: 'communication' }), variant: 'error' }),
      );
      History.goBack();
      return rejectWithValue(error.response.status);
    }
  },
);

export const getCommunicationCampaignCouponThunk = createAsyncThunk(
  'notificationCampaign/getCampaignCoupon',
  async (campaignId: EntityId, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await getCommunicationCampaignCouponApi(campaignId);
      return data;
    } catch (e: any) {
      if (e.response.data.error.status === 'UNABLE_TO_GET_COUPON_BY_COMMUNICATION_CAMPAIGN_ID') {
        return undefined;
      }
      dispatch(
        showMessage({
          message: i18n.t('selectedCampaign.errors.generic', { ns: 'communication' }),
          variant: 'error',
        }),
      );
      return rejectWithValue(e.response.status);
    }
  },
);

export const getClientsForCampaignSearchThunk = createAsyncThunk(
  'notificationCampaign/getClientsForCampaignSearch',
  async (
    payload: {
      searchText: string;
      storeIds?: EntityId[];
      acquiredFrom?: AcquiredFromTypeEnum[];
      clientType?: ClientType[];
      sort?: ClientsSortTypeEnum;
      order?: ClientsSortOrderEnum;
    },
    { rejectWithValue },
  ) => {
    try {
      const { searchText, storeIds, acquiredFrom, clientType, sort, order } = payload;
      const clientsData = await getUserClientsApi(
        sort,
        order,
        1, // page - always page 1 for search
        50, // size - increased limit as suggested
        searchText,
        storeIds,
        acquiredFrom,
        clientType,
      );

      return clientsData;
    } catch (error: any) {
      return rejectWithValue({
        message: error?.response?.data?.message || error?.message || 'An error occurred',
        status: error?.response?.status || 500,
      });
    }
  },
);
